import { useState, useCallback } from 'react'
import { useCV } from '../../hooks/useCV'
import { useTranslation } from '../../contexts/LanguageContext'
import { usePdfGenerator, TemplateType } from '../../hooks/usePdfGenerator'
import { CVPreview } from '../cv-preview/CVPreview'
import { TemplateSelector } from '../cv-preview/TemplateSelector'
import { Button } from '../ui/Button'
import { ReviewButton } from '../ai/ReviewButton'
import { PublishModal } from '../share/PublishModal'
import { DownloadModal } from '../download/DownloadModal'
import { getDefaultPalette } from '../../utils/colorPalettes'
import type { ReviewSection } from '../../types/api'

interface PreviewPaneProps {
  template: TemplateType
  onTemplateChange: (template: TemplateType) => void
  isMobile?: boolean
}

export function PreviewPane({ template, onTemplateChange, isMobile = false }: PreviewPaneProps) {
  const { cv, activeCVId, updateSettings, updateSummary, updatePersonalInfo, updateExperience, updateEducation, updateLanguage } = useCV()
  const { t } = useTranslation()
  const { downloadPdf, isGenerating, error } = usePdfGenerator()
  const [showTemplates, setShowTemplates] = useState(false)
  const [showPublish, setShowPublish] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  const colorPalette = cv.settings?.colorPalette || getDefaultPalette(template)

  const handlePaletteChange = (palette: string) => {
    updateSettings({ colorPalette: palette })
  }

  const handleTemplateChange = (newTemplate: TemplateType) => {
    onTemplateChange(newTemplate)
    // Reset to default palette when changing template
    updateSettings({ colorPalette: getDefaultPalette(newTemplate) })
  }

  const handleDownload = () => {
    setShowDownloadModal(true)
  }

  const executeDownload = useCallback(async () => {
    await downloadPdf(cv, template, colorPalette)
  }, [downloadPdf, cv, template, colorPalette])

  const handleApplyFix = async (section: ReviewSection, _field: string | undefined, fix: string, itemId?: string): Promise<boolean> => {
    // Apply the fix based on section
    switch (section) {
      case 'summary':
        updateSummary(fix)
        return true

      case 'personal':
        // If fix looks like a title (short text without periods), apply to title
        if (fix.length < 100 && !fix.includes('.')) {
          updatePersonalInfo({ title: fix })
          return true
        }
        // Otherwise copy to clipboard
        await copyToClipboard(fix)
        return true

      case 'experience':
        // If we have an itemId, update that specific experience
        if (itemId) {
          updateExperience(itemId, { description: fix })
          return true
        }
        // Fallback: copy to clipboard
        await copyToClipboard(fix)
        return false

      case 'education':
        // If we have an itemId, update that specific education (description or field)
        if (itemId) {
          // Education doesn't have description, but we can update the degree or field
          // For now, we'll assume the fix is for the degree/field combination
          updateEducation(itemId, { field: fix })
          return true
        }
        // Fallback: copy to clipboard
        await copyToClipboard(fix)
        return false

      case 'languages':
        // If we have an itemId, update that specific language
        if (itemId) {
          // Parse the fix to extract name and level if possible
          // Format could be "Inglés: Avanzado" or just the level
          const colonIndex = fix.indexOf(':')
          if (colonIndex > 0) {
            const name = fix.substring(0, colonIndex).trim()
            const levelText = fix.substring(colonIndex + 1).trim().toLowerCase()
            // Map text to valid level values
            const levelMap: Record<string, 'native' | 'fluent' | 'advanced' | 'intermediate' | 'basic'> = {
              'native': 'native', 'nativo': 'native', 'nativa': 'native',
              'fluent': 'fluent', 'fluido': 'fluent', 'fluida': 'fluent',
              'advanced': 'advanced', 'avanzado': 'advanced', 'avanzada': 'advanced',
              'intermediate': 'intermediate', 'intermedio': 'intermediate', 'intermedia': 'intermediate',
              'basic': 'basic', 'básico': 'basic', 'basico': 'basic', 'básica': 'basic', 'basica': 'basic',
            }
            const level = levelMap[levelText]
            if (level) {
              updateLanguage(itemId, { name, level })
            } else {
              // If level not recognized, just update the name
              updateLanguage(itemId, { name })
            }
          } else {
            // Just update the name if no colon found
            updateLanguage(itemId, { name: fix })
          }
          return true
        }
        // Fallback: copy to clipboard
        await copyToClipboard(fix)
        return false

      case 'skills':
        // Skills don't have IDs per item, copy to clipboard
        await copyToClipboard(fix)
        return false

      default:
        await copyToClipboard(fix)
        return false
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = text
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="relative flex flex-col h-full min-h-0 bg-slate-100">
        {/* Mobile Toolbar - Only template selector */}
        <div className="px-3 py-2 bg-white border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className={`w-full flex items-center justify-between min-h-[44px] px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showTemplates
                ? 'bg-primary/5 border-primary text-primary'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                />
              </svg>
              <span>{t.previewPane.template}</span>
            </div>
            <svg
              className={`w-4 h-4 transition-transform ${showTemplates ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Template Selector Dropdown */}
          {showTemplates && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <TemplateSelector
                selected={template}
                onChange={handleTemplateChange}
                selectedPalette={colorPalette}
                onPaletteChange={handlePaletteChange}
              />
            </div>
          )}
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-y-auto p-2">
          <CVPreview cv={cv} template={template} colorPalette={colorPalette} />
        </div>

        {/* Floating Action Buttons */}
        <div className="absolute bottom-4 right-4 flex flex-col gap-3">
          {/* Review Button */}
          <ReviewButton
            cv={cv}
            onApplyFix={handleApplyFix}
            iconOnly
            showConfirmation
            className="w-14 h-14"
          />

          {/* Download Button */}
          <Button
            onClick={handleDownload}
            isLoading={isGenerating}
            disabled={isGenerating}
            className="!rounded-full !p-0 w-14 h-14 shadow-lg"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </Button>
        </div>

        {/* Error message */}
        {error && (
          <div className="absolute top-16 left-4 right-4 bg-red-50 border border-red-200 text-red-600 text-xs px-3 py-2 rounded-lg">
            {error}
          </div>
        )}

        <DownloadModal
          isOpen={showDownloadModal}
          onClose={() => setShowDownloadModal(false)}
          onDownload={executeDownload}
        />
      </div>
    )
  }

  // Desktop Layout
  return (
    <div className="flex flex-col h-full bg-slate-100">
      {/* Toolbar */}
      <div className="px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">{t.previewPane.title}</h2>
            <p className="text-xs text-slate-500">
              {t.previewPane.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {error && (
              <span className="text-xs text-red-600">{error}</span>
            )}
            <ReviewButton cv={cv} onApplyFix={handleApplyFix} />
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                showTemplates
                  ? 'bg-primary/5 border-primary text-primary'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                />
              </svg>
              {t.previewPane.template}
              <svg
                className={`w-3 h-3 transition-transform ${showTemplates ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <button
              onClick={() => setShowPublish(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {t.previewPane.share}
            </button>
            <Button
              onClick={handleDownload}
              isLoading={isGenerating}
              disabled={isGenerating}
              size="sm"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              {t.previewPane.download}
            </Button>
          </div>
        </div>

        {/* Template Selector */}
        {showTemplates && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-700 mb-2">
              {t.previewPane.chooseTemplate}
            </p>
            <TemplateSelector
              selected={template}
              onChange={handleTemplateChange}
              selectedPalette={colorPalette}
              onPaletteChange={handlePaletteChange}
            />
          </div>
        )}
      </div>

      {/* Preview Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-6">
        <CVPreview cv={cv} template={template} colorPalette={colorPalette} />
      </div>

      {/* Publish Modal */}
      <PublishModal
        isOpen={showPublish}
        onClose={() => setShowPublish(false)}
        cv={cv}
        cvId={activeCVId}
        template={template}
        colorPalette={colorPalette}
      />

      {/* Download Modal */}
      <DownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={executeDownload}
      />
    </div>
  )
}
